import {
  ApolloClient,
  InMemoryCache,
  NormalizedCacheObject,
  HttpLink,
  from,
} from "@apollo/client/core";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

import {
  localStorageGetChainCode,
  localStorageGetCustomerCode,
  localStorageGetUserId,
  localStorageClearAuthCache,
} from "@/common/localStorageUtil";
import { fetchAuthSession, signOut } from "@aws-amplify/auth";
import {
  GetCognitoUserStatusQuery,
  GetCognitoUserStatusQueryVariables,
  GetCognitoUserStatusDocument,
  CognitoUserCommonInput,
  OperationInput,
} from "@/generated/graphql";

/**
 * 本 SPA が API キー認証で実行するオペレーション名。
 *
 * 空であることが正しい。本 SPA は未ログインで API を呼ぶ経路を持たないため
 * （全画面が App.vue の <Authenticator> 配下。唯一の例外だった緊急ニュースの
 * setInterval は未認証ガードを追加済み）、常に Bearer で実行する。
 *
 * 公開 API（createTmpMember / getShopByInternalId / getBlockByZipCode）を
 * 未ログインで呼ぶのは別リポジトリの自己登録サービスであり、そちらは独自の
 * Apollo クライアントを持つ。本 SPA の関心事ではない。
 *
 * ここへ追加してはいけない例：getBlockByZipCode は本 SPA でも住所補完に使うが、
 * それはログイン済みの画面からの呼び出しである。追加すると認証済みリクエストに
 * x-api-key が付き、受け入れテスト仕様書 2.5「認証 API に x-api-key が付いた
 * リクエストを 1 件でも発見したら不合格」に抵触する。
 */
const PUBLIC_OPERATIONS = new Set<string>([]);

/** 公開 API（API キーで実行するオペレーション）かどうかを判定する。 */
function isPublicOperation(operationName?: string | null): boolean {
  return PUBLIC_OPERATIONS.has(operationName ?? "");
}

/**
 * 現在の Cognito ID トークンを取得する。
 * 期限切れの場合は Amplify が自動でリフレッシュする。未ログインなら null。
 */
async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch (error) {
    console.warn("[apolloClient] fetchAuthSession に失敗", error);
    return null;
  }
}

/** ネットワークエラーから HTTP ステータスコードを取り出す。取れなければ undefined。 */
function getStatusCode(networkError: unknown): number | undefined {
  if (
    networkError === null ||
    networkError === undefined ||
    typeof networkError !== "object"
  ) {
    return undefined;
  }

  const statusCode = (networkError as { statusCode?: unknown }).statusCode;
  return typeof statusCode === "number" ? statusCode : undefined;
}

/** サインアウト処理の多重実行を防ぐフラグ。 */
let signingOut = false;

/**
 * セッション切れとしてサインアウトし、認証キャッシュを破棄する。
 * 同時に複数のリクエストが 401 になっても 1 回だけ実行する。
 */
async function handleUnauthorized(): Promise<void> {
  if (signingOut) {
    return;
  }
  signingOut = true;

  try {
    await signOut();
    localStorageClearAuthCache();
  } catch (error) {
    console.error("[apolloClient] signOut に失敗", error);
  } finally {
    signingOut = false;
  }
}

/**
 * 認証ヘッダーを付与する link。
 * 公開 API は x-api-key、それ以外は Cognito ID トークン（Bearer）。
 * AppSync は複数の認証ヘッダーが同時に存在するリクエストを拒否するため、
 * 両方を同時に送ってはいけない。
 */
const authLink = setContext(async (operation, prevContext) => {
  if (isPublicOperation(operation.operationName)) {
    return {
      ...prevContext,
      headers: {
        ...prevContext.headers,
        "x-api-key": import.meta.env.VITE_AWS_APPSYNC_APIKEY ?? "",
      },
    };
  }

  const idToken = await getIdToken();

  return {
    ...prevContext,
    headers: {
      ...prevContext.headers,
      ...(idToken === null ? {} : { authorization: `Bearer ${idToken}` }),
    },
  };
});

/**
 * 認証エラーを処理する link。
 * 401（セッション切れ）のみサインアウトへ流し、403（WAF の Block）と
 * 公開 API のエラーは対象外とする。
 */
const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
  // 公開 API はそもそも未ログインで実行するため、サインアウトしてはいけない
  if (isPublicOperation(operation.operationName)) {
    return;
  }

  const statusCode = getStatusCode(networkError);

  // WAF による Block。セッション切れではないためサインアウトしない
  if (statusCode === 403) {
    console.warn("[apolloClient] WAF により Block されました（HTTP 403）");
    return;
  }

  const unauthorized =
    statusCode === 401 ||
    (graphQLErrors ?? []).some(
      (error) =>
        (error as { errorType?: unknown }).errorType === "UnauthorizedException"
    );

  if (unauthorized) {
    console.warn("[apolloClient] セッションが切れたためサインアウトします");
    void handleUnauthorized();
  }
});

/**
 * ApolloClient を生成する。
 */
function createApolloClient(): ApolloClient<NormalizedCacheObject> {
  const uri = import.meta.env.VITE_AWS_APPSYNC_URI;

  return new ApolloClient({
    link: from([errorLink, authLink, new HttpLink({ uri })]),
    cache: new InMemoryCache(),
  });
}

/**
 * ユーザー状態チェック
 */
async function checkCognitoUserStatus(
  client: ApolloClient<NormalizedCacheObject>,
  input: CognitoUserCommonInput
): Promise<void> {
  // オペレータ作成 logInput
  const operationInput: OperationInput = {
    area_code: "10",
    detail: "操作詳細",
    display_category: "オペレータ一覧",
    operation: "オペレータ削除",
    user_id: localStorageGetUserId() || "",
  };

  const result = await client.query<
    GetCognitoUserStatusQuery,
    GetCognitoUserStatusQueryVariables
  >({
    query: GetCognitoUserStatusDocument,
    variables: {
      input,
      logInput: operationInput,
    },
    fetchPolicy: "no-cache",
  });

  const status = result.data?.getCognitoUserStatus?.status;
  if (status === 0) {
    await signOut();
    localStorageClearAuthCache();
  }
}

/**
 * ApolloClient を生成する。
 * 認証ヘッダーは link 側で付与するため、呼び出し側で意識する必要はない。
 * check_user = true の場合のみ、返す前にユーザー状態チェックを行う。
 */
export async function generateApolloClient(
  check_user = false
): Promise<ApolloClient<NormalizedCacheObject>> {
  const client = createApolloClient();

  if (!check_user) {
    return client;
  }

  // 未ログインならユーザー状態チェックは行わない
  const idToken = await getIdToken();
  if (idToken === null) {
    return client;
  }

  const input: CognitoUserCommonInput = {
    customer_code: localStorageGetCustomerCode(),
    chain_code: localStorageGetChainCode(),
    user_id: localStorageGetUserId() || "",
  };

  try {
    await checkCognitoUserStatus(client, input);
  } catch (err) {
    console.log(err);
    // チェックに失敗しても、ひとまずクライアントを返す
  }

  return client;
}
